<template>
  <b-container id="view-company" fluid>
    <h1>{{ company.name }} <small>
        <b-badge pill :variant="company.status === 'confirmed' ? 'success' : 'warning'">{{ company.status }}</b-badge>
        <small v-if="company.meta && company.meta.manualData">
          <b-badge pill variant="dark" v-translate>manual data</b-badge>
        </small>
      </small>
    </h1>
    <b-alert :show="dismissCountDown" dismissible variant="success" @dismissed="dismissCountDown = 0"
      @dismiss-count-down="countDownChanged">
      <h5 v-translate>Company was updated!</h5>
    </b-alert>
    <b-row>
      <b-col class="p-0">
        <b-tabs card v-model="tabIndex">
          <b-tab :title="$gettext('General')" active>
            <b-form @submit.prevent="updateCompany">
              <b-row>
                <b-col>
                  <b-card class="company-info">
                    <b-row class="mb-3 text-center">
                      <b-col>
                        <b-button-group>
                          <b-button size="sm" variant="success" @click="approveCompany"
                            :disabled="company.status === 'confirmed' || company.status === 'rejected'" v-translate>
                            CONFIRM Company
                          </b-button>
                          <b-button size="sm" variant="warning" @click="disableCompany"
                            :disabled="company.status === 'pending' || company.status === 'rejected'" v-translate>
                            DISABLE Company
                          </b-button>
                          <b-button size="sm" variant="danger" @click="rejectCompany"
                            :disabled="company.status === 'rejected' || company.status === 'confirmed'" v-translate>
                            REJECT Company
                          </b-button>
                        </b-button-group>
                      </b-col>
                    </b-row>
                    <b-row class="mb-3 text-center" v-if="!isSelfService">
                      <b-col>
                        <b-button size="sm" v-b-modal.modalFixedPrice v-if="isHeadOperations" v-translate>Set Fixed Time Slot for Company</b-button>
                        <b-modal id="modalFixedPrice" ref="modalFixedPrice" :title="$gettext('Set Fixed Price for Company')"
                          hide-footer>
                          <fixed-price-form :company="company" @success="fixedPriceSuccess"
                            @cancel="fixedPriceCancel" />
                        </b-modal>

                        <pending-fixed-prices :pending-fixed-prices="pendingFixedPrices" />
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <b-form-group id="fieldset-0" :description="$gettext('Brand/Commercial name')">
                          <b-form-input id="brand-name" v-model="company.name" trim required></b-form-input>
                        </b-form-group>
                        <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'document_types').length > 0"
                          :label="$gettext('Document Type')" label-for="company-tin-type">
                          <b-form-select id="company-tin-type" v-model="company.tin_type"
                            :options="accountingCatalogs.filter(x => x.catalog === 'document_types').map(x => { return { text: x.name, value: x.id } })"
                            class="form-control" required>
                            <template #first>
                              <option :value="null" v-translate>Document Type</option>
                            </template>
                          </b-form-select>
                        </b-form-group>
                        <b-form-group id="fieldset-1" :description="$gettext('Company\'s fiscal ID')"
                          :label="accountingCatalogs.findIndex(x => x.catalog === 'document_types' && x.id === company.tin_type) !== -1 ? accountingCatalogs.find(x => x.catalog === 'document_types' && x.id === company.tin_type).name : $gettext('Document Number')"
                          label-for="tin">
                          <b-form-input id="tin" v-model="company.tin" trim
                            :disabled="!isAccountant && company.erp_mapping.length !== 0"></b-form-input>
                        </b-form-group>
                        <b-form-group id="fieldset-1" :description="$gettext('Company\'s fiscal country')" :label="$gettext('Country')"
                          label-for="country">
                          <b-form-select v-model="company.country_iso_code" @change="updateCompanyCountry"
                            :options="countriesSelect" class="mb-3"
                            :disabled="!(isSuperAdmin || isOperatorSupervisor) || hasShippings">
                            <template slot="first">
                              <option :value="null" v-translate>Select a country</option>
                            </template>
                          </b-form-select>
                        </b-form-group>
                        <b-form-group id="business-name" :description="$gettext('Business/Fiscal\'s name')">
                          <b-form-input id="name" v-model="company.business_name" trim></b-form-input>
                        </b-form-group>

                      </b-col>
                      <b-col class="text-center">
                        <div v-show="company.logo">
                          <b-img v-if="company.logo" :src="company.logo" width="100" height="100" blank-color="#777"
                            alt="img" class="m-1" /><br>
                          <b-button size="sm" v-b-modal.modalUploadImage>
                            <translate>Change Logo</translate>
                          </b-button>
                        </div>
                        <div v-show="!company.logo">
                          <b-img v-if="!company.logo" src="https://www.gravatar.com/avatar/default?s=100&r=pg&d=mm"
                            width="100" height="100" blank-color="#777" alt="img" class="m-1" /><br>
                          <b-button v-b-modal.modalUploadImage v-translate>Add Logo</b-button>
                        </div>
                        <div class="mt-3 text-left">
                          <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'customer_types').length > 0"
                            :label="$gettext('Client Type')" label-for="company-entity-type">
                            <b-form-select id="company-entity-type" v-model="company.entity_type"
                              :options="accountingCatalogs.filter(x => x.catalog === 'customer_types').map(x => { return { text: x.name, value: x.id } })"
                              class="form-control" required>
                              <template #first>
                                <option :value="null" v-translate>Client Type</option>
                              </template>
                            </b-form-select>
                          </b-form-group>
                          <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'tax_conditions').length > 0"
                            :label="$gettext('Contributor Type')" label-for="company-contributor-type">
                            <b-form-select id="company-contributor-type" v-model="company.contributor_type"
                              :options="accountingCatalogs.filter(x => x.catalog === 'tax_conditions').map(x => { return { text: x.name, value: x.id } })"
                              class="form-control" required>
                              <template #first>
                                <option :value="null" v-translate>Contributor Type</option>
                              </template>
                            </b-form-select>
                          </b-form-group>
                        </div>
                      </b-col>
                    </b-row>
                    <b-row v-if="!isSelfService">
                      <b-col cols="12">
                        <h3 v-translate>Industries</h3>
                        <div class="form-group">
                          <b-form-checkbox-group v-model="marketingSettings">
                            <b-form-checkbox class="w-50 mr-0"
                              v-for="(i, index) in industries.map(x => { return { text: x.description, value: x.id } })"
                              :value="i.value" :key="index">{{ i.text === 'Other' ? $pgettext('fem', 'Other') : i.text }}
                            </b-form-checkbox>
                          </b-form-checkbox-group>
                        </div>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <b-button variant="secondary" type="submit" size="lg" v-translate>Save Changes</b-button>
                      </b-col>
                    </b-row>
                  </b-card>
                </b-col>

                <b-col>
                  <div class="text-center mb-3">
                    <b-button size="sm" variant="dark" v-b-modal.modalCompanyLog @click="fetchCompanyLog(1)"
                      v-translate>Changes Log</b-button>
                    <b-modal id="modalCompanyLog" ref="modalCompanyLog" size="lg" hide-footer @hidden="companyLog = {}">
                      <div slot="modal-title" v-translate>Company Changes Log</div>
                      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
                      <log-list :log="companyLog" @page-changed="fetchCompanyLog($event)" />
                    </b-modal>
                  </div>

                  <comments-box-single-field :box-title="$gettext('Additional Information')"
                    comment-field-name="additional_information" :payload-field="this.company"
                    :endpoint="this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id" @success="getCompany"
                    @delete="getCompany"
                    :add-condition="(isSuperAdmin || isAccountant || isCommercial || isOperatorSupervisor)"
                    :delete-condition="(isSuperAdmin || isAccountant || isCommercial || isOperatorSupervisor)" />

                  <b-card class="company-operators">
                    <h2 v-translate>Operators</h2>
                    <div>
                      <vue-tags-input v-model="operator" :tags="operators" :autocomplete-items="autocompleteOperators"
                        @tags-changed="getCompany" @before-adding-tag="checkOperator"
                        @before-deleting-tag="deleteOperator" :disabled="!isSuperAdmin && !isOperator"
                        :placeholder="$gettext('Add Operator')" />
                    </div>
                  </b-card>
                  <b-card class="company-tags" v-if="!isSelfService">
                    <h2 v-translate>Relationship</h2>
                    <b-form @submit.prevent="updateCompany">
                      <b-form-select v-model="company.relationship"
                        :options="[{ text: $gettext('Client'), value: 'client' }, { text: $gettext('Provider'), value: 'provider' }]"
                        class="w-auto"></b-form-select>
                      <b-button size="sm" variant="secondary" type="submit" v-translate>
                        Change
                      </b-button>
                    </b-form>
                  </b-card>
                  <b-card>
                    <h2 v-translate>Company Segment</h2>
                    <b-form @submit.prevent="updateCompany">
                      <b-form-select v-model="company.companies_segment_id" :options="companiesSegmentsSelect"
                        class="w-auto" :disabled="!isHeadSales && !isSuperAdmin && !isAdmin">
                        <template #first>
                          <b-form-select-option :value="null"><translate>No segment selected</translate></b-form-select-option>
                        </template>
                      </b-form-select>
                      <b-button size="sm" variant="secondary" type="submit" v-if="isHeadSales || isAdmin || isSuperAdmin" v-translate>
                        Change
                      </b-button>
                    </b-form>
                  </b-card>
                  <b-card v-if="!isSelfService && company.relationship === 'client'">
                    <h2 v-translate>Ownership</h2>
                    <b-form @submit.prevent="updateCompany"
                      v-if="!company.hunter_seller_id || (company.hunter_seller_id && prevSpecialSellers.hunter.id)">
                      <b-form-group label="Hunter" label-for="hunter">
                        <div v-if="!prevSpecialSellers.hunter.id">
                          <user-autocomplete :restrict-to-roles="['commercial', 'head-sales']"
                            @selected="setSpecialSeller('hunter', $event)"
                            v-if="isCommercial || isHeadSales || isAccountant || isSuperAdmin || isAdmin" />
                          <span class="text-warning" v-else v-translate>Must be set by a Commercial, Head of Sales or Accountant</span>
                        </div>
                        <div style="position: relative" v-else>
                          <span class="bg-danger text-white d-block text-center"
                            style="cursor: pointer; width: 1rem; height: 1rem; line-height: 1rem; position: absolute; top: 0; left: 0"
                            href="#" @click.prevent="setSpecialSeller('hunter', {})">×</span>
                          <user-card :user="prevSpecialSellers.hunter" />
                        </div>
                      </b-form-group>
                      <b-button size="sm" variant="secondary" type="submit"
                        v-if="isCommercial || isHeadSales || isAccountant || isSuperAdmin || isAdmin" v-translate>
                        Set Hunter
                      </b-button>
                    </b-form>
                    <div v-else>
                      <h4 v-translate>Hunter</h4>
                      <user-card :user="specialSellers.find(x => x.id === company.hunter_seller_id)" />
                      <b-button size="sm" variant="danger" type="button" @click.prevent="deleteSpecialSeller('hunter')"
                        v-if="isSuperAdmin || isAdmin" v-translate>
                        DELETE Hunter
                      </b-button>
                    </div>
                    <b-form @submit.prevent="updateCompany"
                      v-if="!company.regional_seller_id || (company.regional_seller_id && prevSpecialSellers.regional.id)">
                      <b-form-group label="Regional" label-for="regional">
                        <div v-if="!prevSpecialSellers.regional.id">
                          <user-autocomplete :restrict-to-roles="['commercial', 'head-sales']"
                            @selected="setSpecialSeller('regional', $event)"
                            v-if="isCommercial || isHeadSales || isAccountant || isSuperAdmin || isAdmin" />
                          <span class="text-warning" v-else v-translate>Must be set by a Commercial, Head of Sales or Accountant</span>
                        </div>
                        <div style="position: relative" v-else>
                          <span class="bg-danger text-white d-block text-center"
                            style="cursor: pointer; width: 1rem; height: 1rem; line-height: 1rem; position: absolute; top: 0; left: 0"
                            href="#" @click.prevent="setSpecialSeller('regional', {})">×</span>
                          <user-card :user="prevSpecialSellers.regional" />
                        </div>
                      </b-form-group>
                      <b-button size="sm" variant="secondary" type="submit"
                        v-if="isCommercial || isHeadSales || isAccountant || isSuperAdmin || isAdmin" v-translate>
                        Set Regional
                      </b-button>
                    </b-form>
                    <div v-else>
                      <h4 v-translate>Regional</h4>
                      <user-card :user="specialSellers.find(x => x.id === company.regional_seller_id)" />
                      <b-button size="sm" variant="danger" type="button"
                        @click.prevent="deleteSpecialSeller('regional')" v-if="isSuperAdmin || isAdmin" v-translate>
                        DELETE Regional
                      </b-button>
                    </div>
                  </b-card>
                  <b-card class="company-tags" v-if="!isSelfService">
                    <h2 v-translate>Tags</h2>
                    <div>
                      <vue-tags-input v-model="tag" :tags="tags" :autocomplete-items="autocompleteTags"
                        @tags-changed="getCompany" @before-adding-tag="checkTag" @before-deleting-tag="deleteTag"
                        :disabled="!isSuperAdmin && !isOperatorSupervisor" />
                    </div>
                  </b-card>

                </b-col>
              </b-row>

              <b-card>
                <h2 v-translate>Company Contacts</h2>
                <b-row>
                  <b-col>
                    <h3 v-translate>Commercial</h3>
                    <b-form-group id="fieldset-2" :description="$gettext('Company\'s contact person')" :label="$gettext('Contact name')"
                      label-for="contact-name">
                      <b-form-input id="contact-name" v-model="company.contact_name" trim required></b-form-input>
                    </b-form-group>
                    <b-form-group id="fieldset-3" :description="$gettext('Company\'s email')" :label="$gettext('E-mail')"
                      label-for="contact-email">
                      <b-form-input id="contact-email" type="email" v-model="company.email" trim
                        required></b-form-input>
                    </b-form-group>
                    <b-form-group id="fieldset-4" :description="$gettext('Company\'s phone (Intl. format)')" :label="$gettext('Phone')"
                      label-for="contact-phone">
                      <cleave id="contact-phone" ref="phoneControl"
                        :options="{ phone: true, phoneRegionCode: this.$cookies.get('country').toLowerCase() }"
                        class="form-control" v-model="company.phone" v-if="company.phone"></cleave>
                      <b-button size="sm" v-if="company.phone" style="padding: 0.1rem 1rem;" class="mt-1"
                        variant="secondary" :href="$options.filters.whatsappize(company.phone)">
                        WhatsApp
                      </b-button>
                    </b-form-group>
                    <b-form-group id="fieldset-5" :description="$gettext('Company\'s address')" :label="$gettext('Fiscal address')"
                      label-for="contact-address">
                      <b-form-textarea id="contact-address" v-model="company.address" rows="3" max-rows="6"
                        required></b-form-textarea>
                    </b-form-group>
                    <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'states').length > 0"
                      :label="$gettext('Province/State')" label-for="company-address-state">
                      <b-form-select id="company-address-state" v-model="company.address_state"
                        :options="accountingCatalogs.filter(x => x.catalog === 'states').map(x => { return { text: x.name, value: x.id } })"
                        class="form-control" required>
                        <template #first>
                          <option :value="null" v-translate>Province/State</option>
                        </template>
                      </b-form-select>
                    </b-form-group>
                    <hr>
                    <h3 class="mt-4" v-translate>Technical <small class="text-muted">(optional)</small></h3>
                    <p><small v-translate>Note: If there is not a Technical Contact all technical communications (webhooks/API applications/technical news/etc.) will be sent to the Commercial Contact.</small></p>
                    <b-form-group :description="$gettext('Technical Person\'s Name')" :label="$gettext('Name')"
                      label-for="technical-contact-name">
                      <b-form-input id="technical-contact-name" type="text" v-model="company.technical_contact_name"
                        trim></b-form-input>
                    </b-form-group>
                    <b-form-group :description="$gettext('Technical Person\'s E-mail')" :label="$gettext('E-mail')"
                      label-for="technical-contact-email">
                      <b-form-input id="technical-contact-email" type="email" v-model="company.technical_contact_email"
                        trim></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col>
                    <h3 v-translate>Billing</h3>
                    <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'payment_periods').length > 0"
                      :label="$gettext('Payment Period')" label-for="company-payment-period">
                      <b-form-select id="company-payment-period" v-model="company.payment_period"
                        :options="accountingCatalogs.filter(x => x.catalog === 'payment_periods').map(x => { return { text: x.name, value: x.id } })"
                        class="form-control">
                        <template #first>
                          <option :value="null" v-translate>Select</option>
                        </template>
                      </b-form-select>
                    </b-form-group>
                    <b-form-group v-if="accountingCatalogs.filter(x => x.catalog === 'invoicing_programs').length > 0"
                      :label="$gettext('Invoicing Program')" label-for="company-invoicing-program">
                      <b-form-select id="company-invoicing-program" v-model="company.invoicing_program"
                        :options="accountingCatalogs.filter(x => x.catalog === 'invoicing_programs').map(x => { return { text: x.name, value: x.id } })"
                        class="form-control">
                        <template #first>
                          <option :value="null" v-translate>Select</option>
                        </template>
                      </b-form-select>
                    </b-form-group>
                    <b-form-group v-if="isMoova" :label="$gettext('Invoicing Management Method')"
                      label-for="company-invoicing-process">
                      <b-form-select id="company-invoicing-process" v-model="company.invoicing_process"
                        :options="['prepaid', 'automatic', 'manual']" class="form-control">
                        <template #first>
                          <option :value="null" v-translate>Select</option>
                        </template>
                      </b-form-select>
                    </b-form-group>
                    <b-form-group v-if="isMoova">
                      <b-form-checkbox switch size="lg" v-model="company.requires_purchase_order" :value="1"
                        :unchecked-value="0">
                        <translate>Requires Purchase Order</translate>
                      </b-form-checkbox>
                    </b-form-group>
                    <b-form-group id="fieldset-2" :description="$gettext('Company\'s contact name')" :label="$gettext('Contact name')"
                      label-for="billing-name" v-if="!isSelfService">
                      <b-form-input id="billing-name" v-model="company.billing_details.name" trim
                        required></b-form-input>
                    </b-form-group>
                    <b-form-group id="fieldset-2" :description="$gettext('Company\'s contact last name')" :label="$gettext('Contact last name')"
                      label-for="contact-last-name" v-if="!isSelfService">
                      <b-form-input id="contact-last-name" v-model="company.billing_details.last_name" trim
                        required></b-form-input>
                    </b-form-group>
                    <b-form-group id="fieldset-4" :description="$gettext('Company\'s phone (Intl. format)')" :label="$gettext('Phone')"
                      label-for="billing-phone" v-if="!isSelfService">
                      <cleave id="billing-phone" ref="phoneControl"
                        :options="{ phone: true, phoneRegionCode: this.$cookies.get('country').toLowerCase() }"
                        class="form-control" v-model="company.billing_details.phone" v-if="company.billing_details">
                      </cleave>
                      <b-button size="sm" v-if="company.billing_details && company.billing_details.phone"
                        style="padding: 0.1rem 1rem;" class="mt-1" variant="secondary"
                        :href="$options.filters.whatsappize(company.billing_details.phone)">
                        WhatsApp
                      </b-button>
                    </b-form-group>
                    <label class="d-block" v-if="!isSelfService" v-translate>Billing emails</label>
                    <div class="form-group" v-for="(item, index) in company.billing_details.emails" :key="index"
                      v-if="!isSelfService">
                      <b-input-group>
                        <b-input type="email" :placeholder="$gettext('E-mail')" class="form-control"
                          :id="'company__billing__email' + index" v-model="company.billing_details.emails[index]"
                          required />
                        <b-input-group-append>
                          <b-button size="sm" variant="danger" @click="removeBillingDetailsEmail(index)"
                            style="padding-top: initial; padding-bottom: initial;">
                            <strong>✕</strong>
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                    </div>
                    <form @submit.prevent="addBillingDetailsEmail"
                      v-if="!isSelfService && company.billing_details.emails.length < 5">
                      <b-input-group>
                        <b-input type="email" :placeholder="$gettext('E-mail')" class="form-control"
                          id="company__billing__email" v-model="newBillingDetailsEmail"
                          :required="company.billing_details.emails.length === 0" />
                        <b-input-group-append>
                          <b-button size="sm" variant="success" :disabled="newBillingDetailsEmail === ''" type="submit"
                            style="padding-top: initial; padding-bottom: initial;">
                            <strong>＋</strong>
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                      <b-form-text v-translate>Up to 5 e-mail addresses that will receive our invoices.</b-form-text>
                    </form>
                    <hr>
                    <h3 class="mt-4" v-translate>Operations <small class="text-muted">(optional)</small></h3>
                    <p><small v-translate>Note: If there is not a Operations Contact all communications about operations (pending pickups/etc.) will be sent to the Commercial Contact.</small></p>
                    <b-form-group :description="$gettext('Operations Person\'s Name')" :label="$gettext('Name')"
                      label-for="operations-contact-name">
                      <b-form-input id="operations-contact-name" type="text" v-model="company.operations_contact_name"
                        trim></b-form-input>
                    </b-form-group>
                    <b-form-group :description="$gettext('Operations Person\'s E-mail')" :label="$gettext('E-mail')"
                      label-for="operations-contact-email">
                      <b-form-input id="operations-contact-email" type="email"
                        v-model="company.operations_contact_email" trim></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <b-button variant="secondary" type="submit" size="lg" v-translate>Save Changes</b-button>
                  </b-col>
                </b-row>
              </b-card>
            </b-form>
          </b-tab>

          <b-tab :title="$gettext('Stores')" v-if="!isSelfService">
            <h2 v-translate>Stores</h2>
            <b-table striped :items="getCompanyStores" :fields="companyStoresFields" show-empty
              :empty-text="$gettext('There are no stores to show')">
              <template v-slot:cell(place.formatted_address)="row">
                <span class="shipping-address-listings" :title="row.item.place.formatted_address">
                  {{ row.item.place.formatted_address }}
                </span>
              </template>
              <template v-slot:cell(business_hours)="row">
                <span v-html="$options.filters.brize(getReadableSchedule(row.item.schedule))"></span>
              </template>
            </b-table>
          </b-tab>

          <b-tab :title="$gettext('3P Customizations')" v-if="!isSelfService">
            <h2>
              <b-button size="sm" v-if="isSuperAdmin" v-b-modal.modalAdd3pCustomization v-translate>New Customization</b-button>
            </h2>
            <api-list ref="table3pCustomizations" id="table3pCustomizations" type="outsourcing-company-customizations" :title="false" :prePerPage="10" :preFilters="company.relationship === 'client' ? [['company_id', 'eq', company.id]] : [['provider_id', 'eq', company.id]]" />

            <b-modal id="modalAdd3pCustomization" ref="modalAdd3pCustomization" hide-footer size="lg">
              <div slot="modal-title" v-translate>New 3P Customization</div>
              <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
              <api-item-new type="outsourcing-company-customizations" @success="log(); $refs.table3pCustomizations.updateInPlace({}); $refs.modalAdd3pCustomization.hide()" in-modal :pre-data="company.relationship === 'client' ? {company, company_id: company.id} : {provider: company, provider_id: company.id}" />
            </b-modal>
          </b-tab>

          <b-tab :title="$gettext('Quotes')" v-if="company.relationship !== 'provider' && !isSelfService">
            <h2>
              <b-button size="sm" v-if="isSuperAdmin" v-b-modal.modalAddQuote
                @click="editingCompanyFormula = { company_id: company.id }" v-translate>Create New Quote</b-button>
              <b-button size="sm" v-if="isCommercial" v-b-modal.modalAddExistingQuote v-translate>Add Existing Quote</b-button>
            </h2>
            <b-table striped :items="companyFormulas" :fields="companyFormulasFields" show-empty
              :empty-text="$gettext('There are no quotes to show')">
              <template v-slot:cell(quote.size_id)="data">
                {{ data.item.quote.size_id ? shippingSizes.find(x => x.id === data.item.quote.size_id)?.name : $gettext('All') }}
              </template>
              <template v-slot:cell(quote.area_id_from)="data">
                {{ data.item?.quote.area_id_from ? areas.find(x => x.id === data.item.quote.area_id_from)?.name : $gettext('All') }}
              </template>
              <template v-slot:cell(quote.area_id_to)="data">
                {{ data.item?.quote.area_id_to ? areas.find(x => x.id === data.item.quote.area_id_to)?.name : $gettext('All') }}
              </template>
              <template v-slot:cell(quote.platform_id)="data">
                {{ data.item?.quote.platform_id ? platforms.find(x => x.id === data.item.quote.platform_id)?.name : $gettext('All')
                }}
              </template>
              <template v-slot:cell(quote.shipping_type_id)="data">
                {{ data.item?.quote.shipping_type_id ? shippingTypes.find(x => x.id ===
                  data.item.quote.shipping_type_id).name : $gettext('All') }}
              </template>
              <template v-slot:cell(actions)="data">
                <b-button size="sm" v-b-modal.modalEditCompanyQuote @click="editingCompanyFormula = data.item.quote"
                  style="padding: .5rem .55rem">
                  <font-awesome-icon :icon="['fa', 'pencil-alt']" size="2x"
                    style="font-size: 16px;vertical-align: middle" />
                </b-button>
                <b-button v-if="isCommercial" variant="danger" size="sm"
                  @click="removeCompanyFromQuote(data.item.quote)" style="padding: .5rem .65rem">
                  <font-awesome-icon :icon="['fa', 'trash-alt']" size="2x"
                    style="font-size: 16px;vertical-align: middle" />
                </b-button>
              </template>
            </b-table>
          </b-tab>
          <b-tab :title="$gettext('Provider Quotes')" v-else-if="company.relationship === 'provider' && !isSelfService">
            <b-table striped :items="companyQuoteProviders" :fields="companyQuoteProvidersFields" show-empty
              :empty-text="'There are no quotes to show'">
              <template v-slot:cell(reference)="data">
                {{ data.item.reference }}
              </template>
              <template v-slot:cell(area_id_from)="data">
                {{ data.item.area_id_from ? areas.find(x => x.id === data.item.area_id_from).name : $gettext('All') }}
              </template>
              <template v-slot:cell(area_id_to)="data">
                {{ data.item.area_id_to ? areas.find(x => x.id === data.item.area_id_to).name : $gettext('All') }}
              </template>
              <template v-slot:cell(formula)="data">
                {{ data.item.formula }}
              </template>
              <template v-slot:cell(date_from)="data">
                {{data.item.date_from ? moment.utc(data.item.date_from).tz($tz).format('YYYY-MM-DD') : ''}}<br>
                {{data.item.date_from ? moment.utc(data.item.date_from).tz($tz).format('HH:mm:ss') : ''}}
              </template>
              <template v-slot:cell(date_to)="data">
                {{data.item.date_to ? moment.utc(data.item.date_to).tz($tz).format('YYYY-MM-DD') : ''}}<br>
                {{data.item.date_to ? moment.utc(data.item.date_to).tz($tz).format('HH:mm:ss') : ''}}
              </template>
            </b-table>
          </b-tab>

          <b-tab :title="$gettext('Service Quotes')" v-if="!isSelfService">
            <h2>
              <b-button size="sm" v-if="isSuperAdmin" v-b-modal.modalAddQuoteServices
                @click="editingCompanyFormula = { company_id: company.id }" v-translate>Create New Quote</b-button>
            </h2>
            <b-table striped :items="companyQuoteServices" :fields="companyQuoteServicesFields" show-empty
              :empty-text="'There are no quotes to show'">
              <template v-slot:cell(service_id)="data">
                {{ data.item.service.description || data.item.service.name }}
              </template>
              <template v-slot:cell(quote.area_id_from)="data">
                {{ data.item.quote.area_id_from ? areas.find(x => x.id === data.item.quote.area_id_from).name : $gettext('All') }}
              </template>
              <template v-slot:cell(quote.area_id_to)="data">
                {{ data.item.quote.area_id_to ? areas.find(x => x.id === data.item.quote.area_id_to).name : $gettext('All') }}
              </template>
              <template v-slot:cell(quote.platform_id)="data">
                {{ data.item.quote.platform_id ? platforms.find(x => x.id === data.item.quote.platform_id).name : $gettext('All')
                }}
              </template>
              <template v-slot:cell(quote.shipping_type_id)="data">
                {{ data.item.quote.shipping_type_id ? shippingTypes.find(x => x.id ===
                  data.item.quote.shipping_type_id).name : $gettext('All') }}
              </template>
              <template v-slot:cell(actions)="data">
                <b-button size="sm" v-b-modal.modalEditCompanyQuoteServices @click="editingCompanyFormula = data.item"
                  style="padding: .5rem .55rem">
                  <font-awesome-icon :icon="['fa', 'pencil-alt']" size="2x"
                    style="font-size: 16px;vertical-align: middle" />
                </b-button>
                <b-button v-if="isCommercial" variant="danger" size="sm"
                  @click="deleteCompanyQuoteServices(data.item.id)" style="padding: .5rem .65rem">
                  <font-awesome-icon :icon="['fa', 'trash-alt']" size="2x"
                    style="font-size: 16px;vertical-align: middle" />
                </b-button>
              </template>
            </b-table>
          </b-tab>

          <b-tab :title="$gettext('Users')">
            <b-table stacked="md" foot-clone :items="companyUsers" :fields="companyUsersFields" show-empty
              :empty-text="$gettext('There are no users to show')">

              <div slot="table-busy" class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong v-translate>Loading...</strong>
              </div>

              <template v-slot:cell(id)="data"><a :href="'/edit/users/' + data.value" target="_blank">{{ data.value |
                  uuid }}</a> <a href="#" @click.prevent="copyToClipboard(data.value)" class="text-secondary"
                  :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']"
                    size="1x" /></a></template>

              <template v-slot:cell(profile_picture)="row">
                <img
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                  class="rounded" v-b-tooltip.html
                  :title="'<img src=data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII= style=\'background: transparent url(' + (row.item.profile_picture || 'https://www.gravatar.com/avatar/default?s=150&r=pg&d=mm') + ') no-repeat 50% 50%/cover;\' width=150 height=150 />'"
                  :style="'display: inline-block; width: 50px; height: 50px; background: transparent url(' + (row.item.profile_picture || 'https://www.gravatar.com/avatar/default?s=50&r=pg&d=mm') + ') no-repeat 50% 50%/cover;'"
                  alt="Avatar" />
              </template>

              <template v-slot:cell(name)="row">
                <strong>{{ row.item.name }}</strong>
                <div v-if="row.item.roles" style="width: 220px;">
                  <span v-for="(item, index) in row.item.roles" :key="index">
                    <b-badge variant="dark">{{ item.name }}</b-badge>&nbsp;
                  </span>
                </div>
              </template>

              <template v-slot:cell(email)="row">
                <a :href="'mailto://' + row.item.email">{{ row.item.email }}</a>
                <div v-if="row.item.tags && row.item.tags.length > 0" style="width: 220px;">
                  <span v-for="(item, index) in row.item.tags" :key="index">
                    <b-badge pill variant="danger">{{ item.name }}</b-badge>&nbsp;
                  </span>
                </div>
              </template>

              <template v-slot:cell(phone)="row">
                <div v-if="row.item.phone">
                  {{ row.item.phone | phone(row.item.country_iso_code) }}<br>
                  <a class="btn btn-sm btn-secondary" style="padding: 0.1rem 1rem;" v-if="row.item.phone"
                    :href="'tel://' + row.item.phone" v-translate>Call</a>
                  <a class="btn btn-sm btn-secondary" style="padding: 0.1rem 1rem;" v-if="row.item.phone"
                    :href="$options.filters.whatsappize(row.item.phone)">WhatsApp</a>
                </div>
              </template>

              <template v-slot:cell(active)="row" class="text-center">
                <span :title="row.item.active ? 'Enabled' : 'Disabled'"
                  style="display: inline-block; width:15px; height:15px; border-radius: 100px;"
                  :class="row.item.active ? 'bg-success' : 'bg-danger'"></span>
              </template>

              <template v-slot:cell(company_id)="row">
                <div v-if="row.item.company_id">
                  <a :href="'/edit/companies/' + row.item.company_id" target="_blank">#{{ row.item.company_id }}</a>
                  {{ row.item.company.name }}
                  <div v-if="row.item.company.tags && row.item.company.tags.length > 0" style="width: 220px;">
                    <span v-for="(item, index) in row.item.company.tags" :key="index">
                      <b-badge pill variant="danger">{{ item.name }}</b-badge>&nbsp;
                    </span>
                  </div>
                </div>
              </template>

              <template v-slot:cell(updated_at)="row">
                {{ moment.utc(row.item.updated_at).tz($tz).format('YYYY-MM-DD') }}<br>
                {{ moment.utc(row.item.updated_at).tz($tz).format('HH:mm:ss') }}
              </template>

              <template v-slot:cell(created_at)="row">
                {{ moment.utc(row.item.created_at).tz($tz).format('YYYY-MM-DD') }}<br>
                {{ moment.utc(row.item.created_at).tz($tz).format('HH:mm:ss') }}
              </template>

              <template v-slot:cell(courier)="row">
                <div v-if="row.item.courier">
                  <user-card :user='row.item.courier' :profile-picture="false" :with-titles="false"
                    :vehicle-types-prop="vehicleTypes" />
                </div>
              </template>

              <template v-slot:cell(actions)="row">
                <b-button pill size="sm" @click.stop="row.toggleDetails" style="padding: .5rem">
                  <font-awesome-icon v-if="row.detailsShowing" :icon="['far', 'eye-slash']" size="2x"
                    style="font-size: 16px;vertical-align: middle" />
                  <font-awesome-icon v-else :icon="['far', 'eye']" size="2x"
                    style="font-size: 16px;vertical-align: middle" />
                </b-button>
              </template>

              <template v-slot:row-details="row">
                <b-card>
                  <ul>
                    <li v-for="(value, key) in row.item" :key="key"
                      v-if="value && value.length !== 0 && value !== 'undefined' && value !== null">{{ key }}: <span
                        v-html="key.indexOf('phone') === -1 ? isJson(value) : value.toString()"></span></li>
                  </ul>
                </b-card>
              </template>

            </b-table>
          </b-tab>

          <b-tab :title="$gettext('Sellers')" v-if="!isSelfService">
            <h2 v-translate>Sellers</h2>
            <sellers-management :company="company" :regions="regionsSelect" @success="getCompany"
              v-if="company && regionsSelect" />
          </b-tab>

          <b-tab :title="$gettext('Company\'s Settings')">
            <b-row>
              <b-col>
                <b-card class="company-tags" v-if="company.account">
                  <h2 v-translate>Balance and Credit Limit</h2>
                  <div v-if="company.erp_mapping && company.erp_mapping.length === 1 && company.erp_mapping[0].meta">
                    <h4 v-translate="{ erpId: company.erp_mapping[0].meta.erpId }">ERP Account ID %{erpId} Information
                    </h4>
                    <b-row>
                      <b-col>
                        <h6 v-translate>Balance</h6>
                        {{ company.erp_mapping[0].meta.balance | currency(company.country_iso_code) }}
                      </b-col>
                      <b-col>
                        <h6 v-translate>Deposit Balance</h6>
                        {{ company.erp_mapping[0].meta.depositBalance | currency(company.country_iso_code) }}
                      </b-col>
                      <b-col>
                        <h6 v-translate>Overdue Balance</h6>
                        {{ company.erp_mapping[0].meta.overdueBalance | currency(company.country_iso_code) }}
                      </b-col>
                      <b-col>
                        <h6 v-translate>Unbilled Orders</h6>
                        {{ company.erp_mapping[0].meta.unbilledOrders | currency(company.country_iso_code) }}
                      </b-col>
                    </b-row>
                    <hr>
                  </div>
                  <b-form @submit.prevent="updateAccount">
                    <h4>
                      <translate>Current Balance:</translate>
                      <span v-if="editingBalance === false">
                        {{ company.account.balance | currency(company.country_iso_code) }}
                        <b-button size="sm" variant="link"
                          @click.prevent="editingBalance = true; balance = company.account.balance"
                          style="padding: .2rem" v-if="isSuperAdmin || isAccountant">
                          <font-awesome-icon icon="edit" />
                        </b-button>
                      </span>
                      <span v-else>
                        <b-input-group size="sm">
                          <b-form-input style="width: 100px" v-model="balance" type="number" step=".01"></b-form-input>
                          <b-input-group-append>
                            <b-button size="sm" variant="success" @click.prevent="updateBalance">
                              <font-awesome-icon icon="check" />
                            </b-button>
                            <b-button size="sm" variant="danger" @click.prevent="editingBalance = false">×</b-button>
                          </b-input-group-append>
                        </b-input-group>
                      </span>
                    </h4>
                    <b-form-group>
                      <b-form-checkbox v-model="creditLimitInfinite">
                        <translate>Infinite Credit Limit?</translate>
                      </b-form-checkbox>
                    </b-form-group>
                    <b-form-group v-if="!creditLimitInfinite">
                      <label for="credit-limit" v-translate>Credit Limit</label>
                      <b-form-input v-model="company.account.credit_limit" type="number"></b-form-input>
                    </b-form-group>
                    <b-form-group v-if="!creditLimitInfinite">
                      <label for="credit-limit-notification" v-translate>Credit Limit Notification</label>
                      <b-form-input v-model="company.account.credit_limit_notification" type="number"></b-form-input>
                    </b-form-group>
                    <b-button size="sm" variant="secondary" type="submit" v-translate>
                      Update
                    </b-button>
                  </b-form>
                </b-card>

                <b-card>
                  <h2>
                    <translate>Company Documents</translate>
                    <b-button size="sm" style="padding: 0.1rem 1rem;" v-b-modal.modalCompanyDocumentUpload
                      v-if="isSuperAdmin || isAccountant || isCommercial || isOperatorSupervisor || isAdmin" v-translate>
                      Upload Document
                    </b-button>
                  </h2>
                  <b-modal size="lg" id="modalCompanyDocumentUpload" ref="modalCompanyDocumentUpload" hide-footer
                    @hide="companyDocument = { full_path: '' }">
                    <div slot="modal-title" v-translate>Upload Company Document</div>
                    <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
                    <form @submit.stop.prevent="createCompanyDocument">
                      <uploader ref="uploader" hide-title accept="*/*"
                        :endpoint="this.$appConfig.api.baseUrl + 'companies/' + company.id + '/files'" required
                        @success="fileUploaded" />
                      <b-button variant="primary" type="submit" v-if="companyDocument.full_path" v-translate>
                        Create Document
                      </b-button>
                    </form>
                  </b-modal>
                  <b-table :items="company.documents"
                    :fields="[{ key: 'type', label: $gettext('Type') }, { key: 'full_path', label: $gettext('Uploaded at') }, { key: 'actions', label: this.$gettext('Actions') }]">
                    <template v-slot:cell(full_path)="row">
                      <a :href="'/download?f=companies/documents/' + row.item.id"
                        target="_blank">{{ row.item.created_at }}</a>
                    </template>
                    <template v-slot:cell(actions)="row">
                      <b-button size="sm" style="padding: 0.1rem 1rem;"
                        :href="'/download?f=companies/documents/' + row.item.id" target="_blank"
                        v-translate>Download</b-button>
                      <b-button size="sm" variant="danger" @click.stop="deleteCompanyDocument(row.item)"
                        style="padding: 0.1rem 0.51rem;" v-if="isSuperAdmin">
                        <font-awesome-icon icon="trash-alt" size="1x" style="font-size: 14px;vertical-align: middle" />
                      </b-button>
                    </template>
                  </b-table>
                </b-card>

              </b-col>
              <b-col>
                <b-card>
                  <h2 v-translate>Cut Hour</h2>
                  <h4 v-translate>Weekdays</h4>
                  <div class="input-group mb-4">
                    <b-input-group class="w-auto">
                      <b-form-input v-model="company.cut_time_week" type="time" step="1800" />
                      <b-input-group-append>
                        <b-button size="sm" type="submit" @click.prevent="updateCutTimeWeek"
                          style="height: calc(1.5em + 0.75rem + 3px); padding: 0 .55rem;">
                          <span v-translate>Update Cut Hour</span>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <span class="m-2" v-translate>or</span>
                    <b-button size="sm" @click.prevent="company.cut_time_week = null; updateCutTimeWeek()"
                      style="height: calc(1.5em + 0.75rem + 3px); padding: 0 .55rem;">
                      <span v-translate>Use Country Default</span>
                    </b-button>
                  </div>
                  <h4 v-translate>Saturdays</h4>
                  <div class="input-group mb-4">
                    <b-input-group class="w-auto">
                      <b-form-input v-model="company.cut_time_saturday" type="time" step="1800" />
                      <b-input-group-append>
                        <b-button size="sm" type="submit" @click.prevent="updateCutTimeSaturday"
                          style="height: calc(1.5em + 0.75rem + 3px); padding: 0 .55rem;">
                          <span v-translate>Update Cut Hour</span>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <span class="m-2" v-translate>or</span>
                    <b-button size="sm" @click.prevent="company.cut_time_saturday = null; updateCutTimeSaturday()"
                      style="height: calc(1.5em + 0.75rem + 3px); padding: 0 .55rem;">
                      <span v-translate>Use Country Default</span>
                    </b-button>
                  </div>
                  <h4 v-translate>Sundays</h4>
                  <div class="input-group mb-4">
                    <b-input-group class="w-auto">
                      <b-form-input v-model="company.cut_time_sunday" type="time" step="1800" />
                      <b-input-group-append>
                        <b-button size="sm" type="submit" @click.prevent="updateCutTimeSunday"
                          style="height: calc(1.5em + 0.75rem + 3px); padding: 0 .55rem;">
                          <span v-translate>Update Cut Hour</span>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <span class="m-2" v-translate>or</span>
                    <b-button size="sm" @click.prevent="company.cut_time_sunday = null; updateCutTimeSunday()"
                      style="height: calc(1.5em + 0.75rem + 3px); padding: 0 .55rem;">
                      <span v-translate>Use Country Default</span>
                    </b-button>
                  </div>
                </b-card>
                <b-card>
                  <h2 v-translate>Delivery Days</h2>
                  <div class="mb-3">
                    <b-form-checkbox v-for="(value, i) of company.delivers_at" v-model="company.delivers_at[i]"
                      :key="i">{{ weekdaysByAbbr[i] }}</b-form-checkbox>
                  </div>
                  <b-button size="sm" @click="updateCompany" v-translate>Update</b-button>
                </b-card>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6">
                <b-card>
                  <div class="form-group">
                    <h2 v-translate>Company Settings</h2>
                    <b-form class="mb-3" @submit.prevent.stop="updateCompanySetting(companySetting)"
                      v-for="(companySetting, index) in companySettings" :key="index">
                      <div v-if="companySetting.data_type === 'switch'">
                        <b-form-checkbox switch v-model="companySetting.companySetting.value" value="1"
                          unchecked-value="0" @input="updateCompanySetting(companySetting)">
                          {{ companySetting.description }}
                        </b-form-checkbox>
                      </div>
                      <div v-if="companySetting.data_type === 'ip_ranges'">
                        <label :for="'company-setting-' + companySetting.id" class="d-block">{{ companySetting.name
                          }}</label>
                        <div v-for="(elem, index2) in companySetting.companySetting.value"
                          v-if="companySetting.companySetting" :key="index2">
                          <input-ip placeholder v-model="companySettings[index].companySetting.value[index2]"
                            :ip="elem.split('/')[0]" :port="elem.split('/')[1] || ''" theme="material" />
                          <b-button size="sm" class="py-0 px-2" variant="danger"
                            @click.prevent="companySettings[index].companySetting.value.splice(index2, 1)"
                            v-show="companySetting.companySetting.value.length > 1">
                            ×
                          </b-button>
                          <b-button size="sm" class="py-0 px-2" variant="success"
                            @click.prevent="companySettings[index].companySetting.value.push('')"
                            v-show="index2 + 1 === companySetting.companySetting.value.length">
                            +1
                          </b-button>
                        </div>
                        <b-input-group-append>
                          <b-button size="sm" class="py-1 px-3" variant="secondary" type="submit"
                            v-if="companySetting.companySetting.id">
                            <translate>Update</translate>
                          </b-button>
                          <b-button size="sm" class="py-1 px-3" variant="secondary" type="submit" v-else>
                            <translate>Set</translate>
                          </b-button>
                          <b-button size="sm" class="py-1 px-3" variant="danger"
                            @click="deleteCompanySetting(companySetting)" v-if="companySetting.companySetting.id">
                            <translate>Delete</translate>
                          </b-button>
                        </b-input-group-append>
                      </div>
                      <div v-if="companySetting.data_type === 'json'">
                        {{ companySetting.description }}
                        <div v-for="(field, index) in Object.keys(companySetting.data_type_validations)" :key="index">
                          <label :for="'company-setting-' + field" class="d-block">{{
                            companySetting.data_type_validations[field].description || field }}</label>
                          <div v-if="companySetting.data_type_validations[field].field_type === 'select'">
                            <b-form-select :id="'company-setting-' + field"
                              v-model="companySetting.companySetting.value[field]"
                              :options="companySetting.data_type_validations[field].options" class="mb-3">
                              <template #first>
                                <option :value="null" v-translate>Select one option</option>
                              </template>
                            </b-form-select>
                          </div>
                          <div v-else-if="companySetting.data_type_validations[field].field_type === 'number'">
                            <b-form-input :id="'company-setting-' + field"
                              v-model="companySetting.companySetting.value[field]" type="number"
                              class="mb-3"></b-form-input>
                          </div>
                        </div>
                        <b-button size="sm" class="py-1 px-3" variant="secondary" type="submit"
                          v-if="companySetting.companySetting.id">
                          <translate>Update</translate>
                        </b-button>
                        <b-button size="sm" class="py-1 px-3" variant="secondary" type="submit" v-else>
                          <translate>Set</translate>
                        </b-button>
                        <b-button size="sm" class="py-1 px-3" variant="danger"
                          @click="deleteCompanySetting(companySetting)" v-if="companySetting.companySetting.id">
                          <translate>Delete</translate>
                        </b-button>
                      </div>
                      <div v-if="companySetting.data_type === 'number' || companySetting.data_type === 'string'">
                        <label :for="'company-setting-' + companySetting.id" class="d-block">{{ companySetting.name
                          }}</label>
                        <b-input-group>
                          <b-form-input class="w-auto" v-model="companySettings[index].companySetting.value"
                            :type="companySetting.data_type === 'number' ? 'number' : 'text'"
                            :step="companySetting.data_type === 'number' ? '.01' : null" required />
                          <b-input-group-append>
                            <b-input-group-text v-if="companySetting.name.indexOf('percentage') !== -1">
                              %
                            </b-input-group-text>
                            <b-button size="sm" variant="secondary" type="submit"
                              v-if="companySetting.companySetting.id">
                              <translate>Update</translate>
                            </b-button>
                            <b-button size="sm" variant="secondary" type="submit" v-else>
                              <translate>Set</translate>
                            </b-button>
                            <b-button size="sm" variant="danger" @click="deleteCompanySetting(companySetting)"
                              v-if="companySetting.companySetting.id">
                              <translate>Delete</translate>
                            </b-button>
                          </b-input-group-append>
                        </b-input-group>
                        <b-form-text>{{ companySetting.description }}</b-form-text>
                      </div>
                    </b-form>
                  </div>

                  <b-form @submit.prevent="setNotifications2">
                    <h4 v-translate>Company Notifications Config <small>(realtime change)</small></h4>
                    <h6 v-translate>Note: Checked means notification is <strong>enabled</strong>.</h6>
                    <div v-for="notificationType in notificationTypes" :key="notificationType.id" class="mb-2">
                      <label><strong>{{ notificationType.name }}</strong> (default:
                        {{ notificationType.media_default }})<br>{{ notificationType.description }}</label>
                      <div v-for="media in Object.keys(notificationType.companySetting)"
                        :key="notificationType.id + media">
                        <b-form-checkbox switch v-model="notificationType.companySetting[media]"
                          @change="changeNotification(notificationType.id, media, $event)">{{ media }}</b-form-checkbox>
                      </div>
                    </div>
                  </b-form>
                </b-card>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab :title="$gettext('GCM Settings')" v-if="!isSelfService">
            <h2 v-translate>GCM Configs</h2>
            <b-form @submit.prevent="updateCompany">
              <b-row>
                <b-col cols="12">
                  <label for="change-status" v-translate>Courier Commission Percentage to show in app</label>
                </b-col>
                <b-col class="align-self-center">
                  <div class="d-inline-block w-auto mr-3">
                    <b-input-group append="%">
                      <b-form-input class="m-0" v-model="company.courier_commission_percentage" type="number" step=".01"
                        min="0" max="100" :disabled="hideCourierCommissionPercentage"
                        :required="hideCourierCommissionPercentage === false" />
                    </b-input-group>
                  </div>
                  <div class="d-inline-block w-auto mr-3">
                    <b-form-checkbox v-model="hideCourierCommissionPercentage" :value="true" :unchecked-value="false">
                      <translate>Hide Courier Commission in App</translate>
                    </b-form-checkbox>
                  </div>
                  <div class="d-inline-block w-auto">
                    <b-button size="sm" variant="secondary" type="submit" v-translate>
                      Update
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </b-form>
            <hr>
            <h2 v-translate>Vehicles</h2>
            <b-table striped :items="company.vehicles" :fields="vehicleFields" show-empty
              :empty-text="$gettext('There are no vehicles to show')">
              <template v-slot:cell(vehicle_type)="data">
                {{ vehicleTypes.find(x => x.id === data.item.vehicle_type_id).name }}
              </template>
              <template v-slot:cell(vehicle_card_picture)="data">
                <a :href="data.value" target="_blank" v-if="data.value" v-translate>YES</a>
                <span v-else v-translate>NO</span>
              </template>
              <template v-slot:cell(insurance_card_picture)="data">
                <a :href="data.value" target="_blank" v-if="data.value" v-translate>YES</a>
                <span v-else v-translate>NO</span>
              </template>
              <template v-slot:cell(actions)="data">
                <b-button size="sm" variant="danger" @click.stop="deleteVehicle(data.item)"
                  style="padding: .5rem .65rem" class="mb-1">
                  <font-awesome-icon icon="trash-alt" size="2x" style="font-size: 16px;vertical-align: middle" />
                </b-button>
                <b-button size="sm" @click.stop="editingVehicle = data.item" v-b-modal.modalEditVehicle
                  style="padding: .5rem .55rem" class="mb-1">
                  <font-awesome-icon :icon="['fa', 'pencil-alt']" size="2x"
                    style="font-size: 16px;vertical-align: middle" />
                </b-button>
              </template>
            </b-table>
            <b-button v-b-modal.modalAddVehicle v-translate>Add Vehicle</b-button>
          </b-tab>

          <b-tab :title="$gettext('Integration Apps')" v-if="!isSelfService">
            <h2>
              <b-button variant="secondary" size="sm" @click.prevent="createDefaultApplication" v-if="!hasDefaultApp" v-translate>
                + Default Application
              </b-button>
              <b-button v-else variant="secondary" size="sm" :to="'/new/applications'" v-translate>+ NEW</b-button>
              <b-button variant="secondary" size="sm" @click.prevent="createMeliApplication" v-translate>
                + Mercado Libre Application
              </b-button>
              <b-button variant="secondary" size="sm" @click.prevent="createMagento2Application" v-translate>
                + Magento 2 Application
              </b-button>
            </h2>
            <api-list type="applications" :title="false" @itemsLoaded="appsCount" :prePerPage="10"
              :preFilters="[['company_id', 'eq', company.id]]" />
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>

    <b-modal id="modalAddQuote" ref="modalAddQuote" size="lg" :hide-footer="true">
      <div slot="modal-title" v-translate>Add New Company Quote</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <quotes-create :baseCompany="company" :isModal="true" @quotecreated="quoteCreated" @cancel="quoteCreated" />
    </b-modal>

    <b-modal id="modalAddExistingQuote" ref="modalAddExistingQuote" size="xl" :hide-footer="true"
      @hidden="resetQuoteSearch">
      <div slot="modal-title" v-translate>Add Existing Company Quote</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <b-row>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-name" v-translate>Quote Name</label>
            <input id="quote-name" v-model.lazy="filter.quoteName" type="text" class="mb-2 col-12 form-control">
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-from" v-translate>Area From</label>
            <b-form-select id="quote-area-from" v-model="filter.quoteAreaFrom" :options="this.areasSelect">
              <option slot="first" :value="null" v-translate>
                All Areas
              </option>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-to" v-translate>Area To</label>
            <b-form-select id="quote-area-to" v-model="filter.quoteAreaTo" :options="this.areasSelect">
              <option slot="first" :value="null" v-translate>
                All Areas
              </option>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-platform" v-translate>Platform</label>
            <b-form-select id="quote-platform" v-model="filter.quotePlatform" :options="this.platformsSelect">
              <option slot="first" :value="null" v-translate>
                All Platforms
              </option>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-shipping-type" v-translate>Shipping Type</label>
            <b-form-select id="quote-shipping-type" v-model="filter.quoteShippingType"
              :options="this.shippingTypesSelect">
              <option slot="first" :value="null" v-translate>
                All Types
              </option>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Size</label>
            <b-form-select id="quote-size" v-model="filter.quoteShippingSizeType" :options="this.shippingSizesSelect">
              <option slot="first" :value="null" v-translate>
                All Sizes
              </option>
            </b-form-select>
          </div>
        </b-col>
      </b-row>
      <b-table id="quotes" stacked="md" foot-clone :items="searchedQuotes" :fields="quotesFields"
        v-if="searchedQuotes.length > 0">
        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong v-translate>Loading...</strong>
        </div>
        <template v-slot:cell(size_id)="data">
          {{ data.item.size_id ? shippingSizes.find(x => x.id === data.item.size_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_from)="data">
          {{ data.item.area_id_from ? areas.find(x => x.id === data.item.area_id_from).name : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_to)="data">
          {{ data.item.area_id_to ? areas.find(x => x.id === data.item.area_id_to).name : $gettext('All') }}
        </template>
        <template v-slot:cell(platform_id)="data">
          {{ data.item.platform_id ? platforms.find(x => x.id === data.item.platform_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(shipping_type_id)="data">
          {{ data.item.shipping_type_id ? shippingTypes.find(x => x.id === data.item.shipping_type_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(actions)="data">
          <b-button size="sm" v-if="data.item.name.toLowerCase().indexOf('default') === -1"
            @click="addCompanyToQuote(data.item)" variant="success"
            style="padding: .5rem .75rem; font-size:16px;line-height:1">
            +
          </b-button>
          <div v-else style="padding: .5rem .75rem; font-size:16px;line-height:1" class="text-success">
            <font-awesome-icon :icon="['fa', 'check']" size="2x" style="font-size: 16px;vertical-align: middle" />
          </div>
        </template>
      </b-table>
    </b-modal>

    <b-modal id="modalEditCompanyQuote" ref="modalEditCompanyQuote" size="lg" :hide-footer="true">
      <div slot="modal-title" v-translate>Edit Company Quote</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <quotes-create :quote-data="{ quotes: editingCompanyFormula }" :baseCompany="company" @quoteupdated="quoteUpdated"
        @cancel="quoteUpdated" :isModal="true" :read-only="!isSuperAdmin" />
    </b-modal>

    <b-modal id="modalAddQuoteServices" ref="modalAddQuoteServices" size="lg" :hide-footer="true">
      <div slot="modal-title" v-translate>Add New Company Service Quote</div>
      <div slot="modal-header-close"><img src="../assets/img/ic_close.svg" /></div>
      <quote-services-create :baseCompany="company" isModal @quotecreated="quoteServicesCreated"
        @cancel="quoteServicesCreated" />
    </b-modal>

    <b-modal id="modalEditCompanyQuoteServices" ref="modalEditCompanyQuoteServices" size="lg" :hide-footer="true">
      <div slot="modal-title" v-translate>Edit Company Service Quote</div>
      <div slot="modal-header-close"><img src="../assets/img/ic_close.svg" /></div>
      <quote-services-create :quote-data="editingCompanyFormula" :baseCompany="company"
        @quoteupdated="quoteServicesUpdated" @cancel="quoteServicesUpdated" isModal :read-only="!isSuperAdmin" />
    </b-modal>

    <b-modal id="modalUploadImage" ref="modalUploadImage" :hide-footer="true">
      <div slot="modal-title" v-translate>Add a new profile image</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <profile-picture-uploader @updateProfileImage="updateCompanyImage" />
    </b-modal>

    <b-modal id="modalAddVehicle" ref="modalAddVehicle" :hide-footer="true">
      <div slot="modal-title" v-translate>Add new vehicle to Company</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <vehicle-form :company="company" @success="$refs.modalAddVehicle.hide(); getCompany();"
        @cancel="$refs.modalAddVehicle.hide();" />
    </b-modal>

    <b-modal id="modalEditVehicle" ref="modalEditVehicle" :hide-footer="true">
      <div slot="modal-title" v-translate>Edit vehicle</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <vehicle-form :company="company" :vehicle="editingVehicle"
        @success="editingVehicle = null; $refs.modalEditVehicle.hide(); getCompany();"
        @cancel="editingVehicle = null; $refs.modalEditVehicle.hide();" />
    </b-modal>

  </b-container>
</template>

<script>
import ProfilePictureUploader from '@/components/ProfilePictureUploader'
import Cleave from 'vue-cleave'
import QuotesCreate from '@/components/QuotesCreate'
import QuoteServicesCreate from '@/components/QuoteServicesCreate'
import ApiList from '@/components/ApiList'
import ApiItemNew from '@/components/ApiItemNew'
import FixedPriceForm from '@/components/FixedPriceForm'
import PendingFixedPrices from '@/components/PendingFixedPrices'
import VueTagsInput from '@johmun/vue-tags-input'
import VehicleForm from '@/components/VehicleForm'
import UserCard from '@/components/UserCard'
import UserAutocomplete from '@/components/autocompletes/UserAutocomplete'
import SellersManagement from '@/components/SellersManagement'
import Uploader from '@/components/Uploader'
import InputIp from '@/components/InputIp'
import LogList from '@/components/LogList'
import CommentsBoxSingleField from '@/components/CommentsBoxSingleField'

export default {
  components: {
    QuotesCreate,
    QuoteServicesCreate,
    ApiList,
    ApiItemNew,
    ProfilePictureUploader,
    Cleave,
    FixedPriceForm,
    PendingFixedPrices,
    VueTagsInput,
    VehicleForm,
    UserCard,
    UserAutocomplete,
    SellersManagement,
    Uploader,
    InputIp,
    LogList,
    CommentsBoxSingleField
  },
  data() {
    return {
      tabIndex: 0,
      company: {},
      companyFormulas: [],
      companyFormulasFields: [
        {
          key: 'quote.name',
          label: this.$gettext('Name')
        },
        {
          key: 'quote.area_id_from',
          label: this.$gettext('From')
        },
        {
          key: 'quote.area_id_to',
          label: this.$gettext('To')
        },
        {
          key: 'quote.quote_formula.formula',
          label: this.$gettext('Formula')
        },
        {
          key: 'quote.platform_id',
          label: this.$gettext('Platform')
        },
        {
          key: 'quote.shipping_type_id',
          label: this.$gettext('Type')
        },
        {
          key: 'quote.size_id',
          label: this.$gettext('Size')
        },
        {
          key: 'actions',
          label: this.$gettext('Actions')
        }
      ],
      quotesFields: [
        {
          key: 'name',
          label: this.$gettext('Name')
        },
        {
          key: 'area_id_from',
          label: this.$gettext('From')
        },
        {
          key: 'area_id_to',
          label: this.$gettext('To')
        },
        {
          key: 'platform_id',
          label: this.$gettext('Platform')
        },
        {
          key: 'shipping_type_id',
          label: this.$gettext('Type')
        },
        {
          key: 'size_id',
          label: this.$gettext('Size')
        },
        {
          key: 'actions',
          label: this.$gettext('Actions')
        }
      ],
      companyQuoteProviders: [],
      companyQuoteProvidersFields: [
        {
          key: 'reference',
          label: this.$gettext('Quote Name')
        },
        {
          key: 'area_id_from',
          label: this.$gettext('Origin Area')
        },
        {
          key: 'area_id_to',
          label: this.$gettext('Destination Area')
        },
        {
          key: 'formula',
          label: this.$gettext('Formula')
        },
        {
          key: 'date_from',
          label: this.$gettext('From Date')
        },
        {
          key: 'date_to',
          label: this.$gettext('To Date')
        }
      ],
      companyQuoteServices: [],
      companyQuoteServicesFields: [
        {
          key: 'service_id',
          label: this.$gettext('Service')
        },
        {
          key: 'price_formula',
          label: this.$gettext('Formula')
        },
        {
          key: 'courier_commission_formula',
          label: this.$gettext('Courier Commission Formula')
        },
        {
          key: 'actions',
          label: this.$gettext('Actions')
        }
      ],
      companyStoresFields: [
        {
          key: 'reference',
          label: this.$gettext('Reference')
        },
        {
          key: 'contact',
          label: this.$gettext('Contact')
        },
        {
          key: 'phone',
          label: this.$gettext('Phone')
        },
        {
          key: 'place.formatted_address',
          label: this.$gettext('Address')
        },
        {
          key: 'business_hours',
          label: this.$gettext('Business Hours')
        }
      ],
      filters: [],
      filter: {
        quoteName: '',
        quoteAreaFrom: null,
        quoteAreaTo: null,
        quotePlatform: null,
        quoteShippingType: null,
        quoteShippingSizeType: null
      },
      searchedQuotes: [],
      areas: [],
      platforms: [],
      shippingTypes: [],
      resetting: false,
      editingCompanyFormula: {},
      hasDefaultApp: false,
      dismissSecs: 10,
      dismissCountDown: 0,
      newBillingDetailsEmail: '',
      hasShippings: true,
      tag: '',
      tags: [],
      pendingFixedPrices: [],
      autocompleteTags: [],
      debounce: null,
      operator: '',
      operators: [],
      autocompleteOperators: [],
      marketingSettings: [],
      editingVehicle: null,
      vehicleFields: [
        {
          key: 'reference',
          label: this.$gettext('Reference')
        },
        {
          key: 'plate_number',
          label: this.$gettext('Plate Number')
        },
        {
          key: 'vehicle_type',
          label: this.$gettext('Type')
        },
        {
          key: 'insurance_expiration_date',
          label: this.$gettext('Insurance Exp.')
        },
        {
          key: 'vehicle_card_picture',
          label: this.$gettext('Vehicle Card')
        },
        {
          key: 'insurance_card_picture',
          label: this.$gettext('Insurance Card')
        },
        {
          key: 'actions',
          label: this.$gettext('Actions')
        }
      ],
      creditLimitInfinite: null,
      balance: 0,
      editingBalance: false,
      hideCourierCommissionPercentage: null,
      companySettings: [],
      companyUsers: [],
      companyUsersFields: [],
      accountingCatalogs: [],
      specialSellers: [],
      prevSpecialSellers: {
        hunter: {},
        regional: {}
      },
      companyDocument: {
        full_path: ''
      },
      companyLog: {}
    }
  },
  computed: {
    isSelfService() {
      return this.company && this.company.companies_segment_id !== 0 && this.companiesSegmentsSelect.find(x => x.value === this.company.companies_segment_id).text === 'SelfService'
    }
  },
  watch: {
    'filter.quoteName': {
      handler(val) {
        if (!this.resetting) {
          if (val !== '') {
            this.filters['quoteName'] = ['name', 'ct', val]
          } else {
            this.filters['quoteName'] = []
          }
          this.searchQuotes()
        }
      },
      deep: true
    },
    'filter.quoteAreaFrom': {
      handler(val) {
        if (!this.resetting) {
          if (val !== null) {
            this.filters['quoteAreaFrom'] = ['area_id_from', 'eq', val]
          } else {
            this.filters['quoteAreaFrom'] = []
          }
          this.searchQuotes()
        }
      },
      deep: true
    },
    'filter.quoteAreaTo': {
      handler(val) {
        if (!this.resetting) {
          if (val !== null) {
            this.filters['quoteAreaTo'] = ['area_id_to', 'eq', val]
          } else {
            this.filters['quoteAreaTo'] = []
          }
          this.searchQuotes()
        }
      },
      deep: true
    },
    'filter.quotePlatform': {
      handler(val) {
        if (!this.resetting) {
          if (val !== null) {
            this.filters['quotePlatform'] = ['platform_id', 'eq', val]
          } else {
            this.filters['quotePlatform'] = []
          }
          this.searchQuotes()
        }
      },
      deep: true
    },
    'filter.quoteShippingType': {
      handler(val) {
        if (!this.resetting) {
          if (val !== null) {
            this.filters['quoteShippingType'] = ['shipping_type_id', 'eq', val]
          } else {
            this.filters['quoteShippingType'] = []
          }
          this.searchQuotes()
        }
      },
      deep: true
    },
    'filter.quoteShippingSizeType': {
      handler(val) {
        if (!this.resetting) {
          if (val !== null) {
            this.filters['quoteShippingSizeType'] = ['size_id', 'eq', val]
          } else {
            this.filters['quoteShippingSizeType'] = []
          }
          this.searchQuotes()
        }
      },
      deep: true
    },
    tag: 'initTagItems',
    creditLimitInfinite(val, oldVal) {
      if (oldVal !== null) {
        if (val) {
          this.company.account.credit_limit = null
          this.company.account.credit_limit_notification = null
        } else {
          this.company.account.credit_limit = 0
          this.company.account.credit_limit_notification = 0
        }
      }
    },
    operator: 'initOperatorItems',
    hideCourierCommissionPercentage(val) {
      if (val) {
        this.company.courier_commission_percentage = null
      }
    },
    'company.courier_commission_percentage': {
      handler(val) {
        if (!val) {
          this.hideCourierCommissionPercentage = true
        }
      },
      deep: true
    },
    tabIndex(val) {
      if (!this.isSelfService && val === 1) {
        this.getCompanyStores()
      } else if (val === 3) {
        if (this.company.relationship === 'client') {
          this.getCompanyQuotes()
        } else {
          this.getCompanyQuoteProviders()
        }
      } else if (val === 4) {
        this.getCompanyQuoteServices()
      } else if (!this.isSelfService && val === 5 || this.isSelfService && val === 1) {
        this.getCompanyUsers()
      }
    }
  },
  created() {
    let countryParams = {
      includes: [
        'notification_configs'
      ]
    }
    this.fetchCountries(countryParams)
    this.fetchRegions()
    this.fetchAreas()
    this.fetchPlatforms()
    this.fetchShippingTypes()
    this.fetchVehicleTypes()
    this.fetchShippingSizes()
    this.fetchNotificationTypes()
    this.fetchIndustries()
    this.fetchCompaniesSegments()
    this.fetchServices()
    this.companyUsersFields = this.$appConfig.models['users'].fields
    this.companyUsersFields.push({key: 'actions', label: this.$gettext('Actions')})
  },
  mounted() {
    this.getCompany()
    this.fetchCompaniesSettings()
      .then(() => {
        this.getCompany()
      })
  },
  methods: {
    setHasShippings(count) {
      this.hasShippings = count && count > 0 || false
    },
    initTagItems() {
      if (this.tag.length < 2) return
      let params = {
        filter_groups: [{
          filters: [
            ['name', 'ct', this.tag]
          ]
        }]
      }

      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        this.$http.get(this.$appConfig.api.baseUrl + 'tags', { params: params })
          .then(response => {
            this.autocompleteTags = response.body.data.map(x => {
              x.text = x.name
              return x
            })
          }).catch(() => console.warn('Oh. Something went wrong'))
      }, 600)
    },
    checkTag(obj) {
      if (!obj.tag.id) {
        this.$http.post(this.$appConfig.api.baseUrl + 'tags', { name: obj.tag.text.toLowerCase() })
          .then(response => {
            obj.tag.id = response.body.id
            obj.tag.text = response.body.name
            this.$http.post(this.$appConfig.api.baseUrl + 'tags-models', {
              tag_id: response.body.id,
              model_type: 'company',
              model_id: this.company.id + ''
            })
              .then(response => {
                obj.addTag()
              })
          }, error => {
            console.log(error)
          })
      } else {
        this.$http.post(this.$appConfig.api.baseUrl + 'tags-models', {
          tag_id: obj.tag.id,
          model_type: 'company',
          model_id: this.company.id + ''
        })
          .then(response => {
            obj.addTag()
          }, error => {
            console.log(error)
          })
      }
    },
    deleteTag(obj) {
      this.$http.delete(this.$appConfig.api.baseUrl + 'tags-models/' + obj.tag.tag_model_id)
        .then(response => {
          obj.deleteTag()
        }, error => {
          console.log(error)
        })
    },
    initOperatorItems() {
      if (this.operator.length < 2) return
      let params = {
        includes: [
          'roles'
        ],
        filter_groups: [
          {
            or: true,
            filters: [
              ['name', 'ct', this.operator],
              ['email', 'ct', this.operator]
            ]
          },
          {
            filters: [
              ['roles.name', 'in', ['operator', 'operator-supervisor', 'super-administrator']]
            ]
          }
        ]
      }

      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        this.$http.get(this.$appConfig.api.baseUrl + 'users', {
          params: params
        })
          .then(response => {
            this.autocompleteOperators = response.body.data.map(x => {
              x.text = x.name
              return x
            })
          }).catch(() => console.warn('Oh. Something went wrong'))
      }, 600)
    },
    checkOperator(obj) {
      this.$http.post(this.$appConfig.api.baseUrl + 'companies-management-users', {
        user_id: obj.tag.id,
        company_id: this.company.id + '',
        relationship: 'operator'
      })
        .then(response => {
          obj.addTag()
        }, error => {
          console.log(error)
        })
    },
    deleteOperator(obj) {
      this.$http.delete(this.$appConfig.api.baseUrl + 'companies-management-users/' + obj.tag.company_management_user_id)
        .then(response => {
          obj.deleteTag()
        }, error => {
          console.log(error)
        })
    },
    updateCompanyCountry() {
      if (
        confirm(this.$gettext('Are you sure you want to CHANGE the Company country?\n\nThis change will be applied instantly.'))
      ) {
        const params = {
          country_iso_code: this.company.country_iso_code
        }
        this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.company.id + '/country', params)
          .then(response => {
            this.getCompany()
          })
      } else {
        this.getCompany()
      }
    },
    getReadableSchedule(schedule) {
      let readable = ''
      let scheduleGroups = this.getScheduleGroups(schedule)
      if (scheduleGroups.length !== 0) {
        // console.log(scheduleGroups)
        scheduleGroups.forEach((group, i) => {
          readable += group.days.join(', ') + '\n'
          group.schedule.forEach((shift, i) => {
            readable += '\t' + '- From ' + shift.from + ' to ' + shift.to + '\n'
          })
        })
      }

      return readable
    },
    getScheduleGroups(collection) {
      let groups = {}
      collection.forEach((schedule, i) => {
        let hasShifts = false
        if (schedule.businessHours) {
          let scheduleSignature = ''
          let groupSchedule = []
          schedule.businessHours.forEach((shift, n) => {
            let hasHours = shift.from && shift.from !== ''
            if (hasHours) {
              hasShifts = true
              groupSchedule.push(shift)
              scheduleSignature += shift.from + '-' + shift.to
            }
          })
          if (hasShifts) {
            if (groups[scheduleSignature]) {
              groups[scheduleSignature].days.push(schedule.dayName)
            } else {
              groups[scheduleSignature] = {
                'days': [schedule.dayName],
                'schedule': groupSchedule
              }
            }
          }
        }
      })
      return Object.values(groups)
    },
    addBillingDetailsEmail() {
      this.company.billing_details.emails.push(this.newBillingDetailsEmail)
      this.newBillingDetailsEmail = ''
    },
    removeBillingDetailsEmail(index) {
      this.company.billing_details.emails.splice(index, 1)
    },
    getQuotesRelations() {
      this.$http.get(this.$appConfig.api.baseUrl + 'platforms')
        .then(response => {
          this.platforms = response.body.data
          this.platforms.unshift({
            id: null,
            name: this.$gettext('All platforms')
          })
        })
      this.$http.get(this.$appConfig.api.baseUrl + 'shipping-types')
        .then(response => {
          this.shippingTypes = response.body.data
          this.shippingTypes.unshift({
            id: null,
            name: this.$gettext('All types')
          })
        })
    },
    async getCompanyUsers() {
      let params = {
        includes: ['tags', 'company.tags', 'platform_user_authorizations.platform'],
        page: 0,
        limit: 500,
        sort: [{
          key: 'id',
          direction: 'asc'
        }],
        filter_groups: [{
          filters: [
            ['company_id', 'eq', this.company.id]
          ]
        }]
      }
      return await this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['users'].endpoint, {
        params: params
      })
        .then(response => {
          this.companyUsers = response.body.data
          return this.companyUsers
        })
    },
    async getCompanyQuotes() {
      let params = {
        includes: ['quote_companies.company'],
        page: 0,
        limit: 500,
        sort: [{
          key: 'id',
          direction: 'asc'
        }],
        filter_groups: [{
          filters: [
            ['quote_companies.company_id', 'eq', this.company.id]
          ]
        }]
      }
      return await this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['quotes'].endpoint, {
        params: params
      })
        .then(response => {
          this.companyFormulas = response.body.data.map(x => {
            return {
              quote: x
            }
          })
          return this.companyFormulas
        })
    },
    async searchQuotes() {
      let params = {
        page: 0,
        limit: 500,
        sort: [{
          key: 'id',
          direction: 'asc'
        }]
      }
      console.log(this.filters)
      if (this.filters) {
        params.filter_groups = [{
          filters: Object.values(this.filters)
        }]
      }
      params.filter_groups.push({
        'filters': [
          ['type', 'eq', 'company']
        ]
      })
      return this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['quotes'].endpoint, {
        params: params
      })
        .then(response => {
          let excludes = this.companyFormulas.map(x => {
            return x.quote_id
          })
          this.searchedQuotes = response.body.data
          for (let i = 0; i < excludes.length; i++) {
            let index = -1
            if ((index = this.searchedQuotes.findIndex(x => x.id === excludes[i])) !== -1) {
              this.searchedQuotes.splice(index, 1)
            }
          }
          return this.searchedQuotes
        })
    },
    resetQuoteSearch() {
      this.resetting = true
      this.searchedQuotes = []
      this.filters = []
      Object.assign(this.$data.filter, this.$options.data.call(this).filter)
      setTimeout(function () {
        this.resetting = false
      }.bind(this), 500)
    },
    addCompanyToQuote(quote) {
      if (
        confirm(this.$gettext('Are you sure you want to ADD this Quote to the current Company?'))
      ) {
        let quoteCurrentCompanies = quote.quote_companies.map(x => x.company_id)
        quoteCurrentCompanies.push(this.company.id)
        this.$http.post(this.$appConfig.api.baseUrl + 'quotes/' + quote.id + '/companies', quoteCurrentCompanies)
          .then(response => {
            this.getCompanyQuotes()
            this.$refs.modalAddExistingQuote.hide()
          })
      }
    },
    removeCompanyFromQuote(quote) {
      if (
        confirm(this.$gettext('Are you sure you want to DELETE this Quote from the current Company?'))
      ) {
        let quoteCurrentCompanies = quote.quote_companies.map(x => x.company_id)
        quoteCurrentCompanies.splice(quoteCurrentCompanies.findIndex(x => x === this.company.id), 1)
        this.$http.post(this.$appConfig.api.baseUrl + 'quotes/' + quote.id + '/companies', quoteCurrentCompanies)
          .then(response => {
            this.getCompanyQuotes()
          })
      }
    },
    async getCompanyQuoteProviders() {
      var params = {
        includes: [
          'area_from',
          'area_to'
        ],
        page: 0,
        limit: 500,
        sort: [{
          key: 'reference',
          direction: 'asc'
        }],
        filter_groups: [{
          filters: [
            ['company_id', 'eq', this.company.id]
          ]
        }]
      }
      return await this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['quote-providers'].endpoint, {
        params: params
      })
        .then(response => {
          this.companyQuoteProviders = response.body.data
          return this.companyQuoteProviders
        })
    },
    async getCompanyQuoteServices() {
      var params = {
        includes: [
          'service'
        ],
        page: 0,
        limit: 500,
        sort: [{
          key: 'id',
          direction: 'asc'
        }],
        filter_groups: [{
          filters: [
            ['company_id', 'eq', this.company.id]
          ]
        }]
      }
      return await this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['quote-services'].endpoint, {
        params: params
      })
        .then(response => {
          this.companyQuoteServices = response.body.data
          // .map(x => {
          //   return {
          //     quote: x
          //   }
          // })
          return this.companyQuoteServices
        })
    },
    async searchQuoteServices() {
      var params = {
        page: 0,
        limit: 500,
        sort: [{
          key: 'id',
          direction: 'asc'
        }]
      }
      if (this.filters) {
        params.filter_groups = [{
          filters: Object.values(this.filters)
        }]
      }
      params.filter_groups.push({
        'filters': [
          ['company_id', 'eq', this.company.id]
        ]
      })
      return this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['quote-services'].endpoint, {
        params: params
      })
        .then(response => {
          return response.body.data
        })
    },
    resetQuoteServicesSearch() {
      this.resetting = true
      this.searchedQuoteServices = []
      this.filters = []
      Object.assign(this.$data.filter, this.$options.data.call(this).filter)
      setTimeout(function () {
        this.resetting = false
      }.bind(this), 500)
    },
    deleteCompanyQuoteServices(id) {
      if (
        confirm(this.$gettext('Are you sure you want to DELETE this Service Quote?\n\nThis action CANNOT be UNDONE.'))
      ) {
        this.$http.delete(this.$appConfig.api.baseUrl + 'quote-services/' + id)
          .then(response => {
            this.getCompanyQuoteServices()
          })
      }
    },
    appsCount(items) {
      if (items.findIndex(x => x.platform_id === null) !== -1) {
        this.hasDefaultApp = true
      }
    },
    createDefaultApplication() {
      let params = {
        name: this.company.name,
        company_id: this.company.id,
        active: true
      }
      this.$http.post(this.$appConfig.api.baseUrl + 'applications', params)
        .then(response => {
          setTimeout(function () {
            this.$root.$emit('bv::refresh::table', 'applications')
          }.bind(this), 1000)
        })
    },
    createMeliApplication() {
      let params = {
        name: this.company.name + ' Mercado Libre',
        company_id: this.company.id,
        platform_id: this.platforms.find(x => x.slug === 'meli').id,
        active: true
      }
      this.$http.post(this.$appConfig.api.baseUrl + 'applications', params)
        .then(response => {
          this.$root.$emit('bv::refresh::table', 'applications')
        })
    },
    createMagento2Application() {
      let params = {
        name: this.company.name + ' Magento 2',
        company_id: this.company.id,
        platform_id: this.platforms.find(x => x.slug === 'magento2').id,
        active: true
      }
      this.$http.post(this.$appConfig.api.baseUrl + 'applications', params)
        .then(response => {
          this.$root.$emit('bv::refresh::table', 'applications')
        })
    },
    getCompany() {
      this.company = {}
      this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id, {
        params: {
          includes: [
            'users',
            'fixed_amounts.fixed_tour',
            'tags',
            'notification_configs',
            'marketing_settings.setting',
            'vehicles',
            'operators',
            'sellers',
            'settings',
            'erpMapping',
            'documents',
            'segment'
          ]
        }
      })
        .then(response => {
          this.company = response.body
          this.hideCourierCommissionPercentage = !this.company.courier_commission_percentage
          this.companyUsers = response.body.users
          this.fetchRegions(this.company.country_iso_code)
          if (!this.company.billing_details) {
            this.company.billing_details = {
              name: '',
              last_name: '',
              phone: '',
              emails: []
            }
          }
          if (!this.company.meta) {
            this.company.meta = []
          }

          if (this.isMoova) {
            this.fetchAccountingStuff()
          }

          this.pendingFixedPrices = response.body.fixed_amounts.filter(x => x.status === 'pending')
          this.tags = this.company.tags.map(x => {
            x.text = x.name
            return x
          })
          this.operators = this.company.operators.map(x => {
            x.text = x.name
            return x
          })

          if (this.company.hunter_seller_id || this.company.regional_seller_id) {
            this.fetchSellers()
          }

          const countryExcludedNotifications = this.countries.find(x => x.iso_code === response.body.country_iso_code).notification_configs.map(x => {
            return x.notification_type_id
          })

          this.company.notification_configs.reverse()
          this.notificationTypes = this.notificationTypes.filter(x => countryExcludedNotifications.indexOf(x.id) === -1).map(x => {
            x.companySetting = {}
            x.companySetting.email = this.company.notification_configs.findIndex(y => y.notification_type_id === x.id && y.media === 'email' && y.enabled === 1) !== -1 || this.company.notification_configs.findIndex(y => y.notification_type_id === x.id && y.media === 'email') === -1
            x.companySetting.whatsapp = this.company.notification_configs.findIndex(y => y.notification_type_id === x.id && y.media === 'whatsapp' && y.enabled === 1) !== -1
            return x
          })

          this.marketingSettings = response.body.marketing_settings.map(x => {
            return x.setting.id
          })

          this.creditLimitInfinite = this.company.account?.credit_limit === null

          this.companySettings = []
          this.companySettings = [...new Set(this.companiesSettings.map(x => {
            let defaultValue
            switch (x.data_type) {
              case 'ip_ranges':
                defaultValue = ['...']
                break
              case 'json':
                defaultValue = Object.keys(x.data_type_validations).reduce((a, v) => ({ ...a, [v]: null }), {})
                break
              default:
                defaultValue = ''
            }
            x.companySetting = this.company.settings.length > 0 && this.company.settings.findIndex(y => x.name === y.name) !== -1
              ? this.company.settings.find(y => x.name === y.name).pivot
              : {
                value: defaultValue,
                setting_id: x.id,
                company_id: this.company.id
              }
            x.companySetting.id = this.company.settings.length > 0 && this.company.settings.findIndex(y => x.name === y.name) !== -1 ? this.company.settings.find(y => x.name === y.name).id : null
            return x
          }))]
          this.company.stores = []
        })
    },
    approveCompany() {
      if (!this.isSelfService && this.company.relationship === 'client') {
        if (this.company.hunter_seller_id) {
          this.updateCompany()
            .then(() => {
              this.$http.put(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/approve')
                .then(response => {
                  this.getCompany()
                })
            })
        } else {
          let obj = {
            title: this.$gettext('ERROR'),
            message: this.$gettext('An Ownership HUNTER must exists in order to approve a Company'),
            type: 'error'
          }
          this.$parent.$refs.simplert.openSimplert(obj)
        }
      } else {
        this.updateCompany()
          .then(() => {
            if (!this.isSelfService) {
              this.$http.put(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/approve')
                .then(response => {
                  this.getCompany()
                })
            } else {
              let companyAdminUserId = this.companyUsers.find(
                x => x.roles.find(
                  y => y.name === 'company-admin' || y.name === 'self-service-admin'
                )
              ).id

              if (companyAdminUserId) {
                this.$http.put(this.$appConfig.api.baseUrl + 'user/' + companyAdminUserId + '/companies/' + this.$route.params.id + '/approve')
                  .then(() => {
                    this.getCompany()
                  })
              }
            }
          })
      }
    },
    disableCompany() {
      this.$http.put(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/disable')
        .then(response => {
          this.getCompany()
        })
    },
    rejectCompany() {
      if (
        confirm(this.$gettext('Are you sure you want to REJECT the current Company? This action will completely DELETE any Company record and Users and CANNOT be UNDONE.'))
      ) {
        this.$http.delete(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/reject')
          .then(response => {
            this.$router.push({
              name: 'modelList',
              params: {
                type: 'companies'
              },
              query: {
                deleted: 'true'
              }
            })
          })
      }
    },
    async fetchSellers() {
      let sellerIds = [this.company.hunter_seller_id || null, this.company.regional_seller_id || null].filter(Boolean)

      let params = {
        filter_groups: [
          {
            filters: [
              ['id', 'in', sellerIds]
            ]
          }
        ]
      }
      return this.$http.get(this.$appConfig.api.baseUrl + 'users', { params: params })
        .then(response => {
          this.specialSellers = response.body.data
          return this.specialSellers
        })
    },
    setSpecialSeller(type, event) {
      this.prevSpecialSellers[type] = event
      this.company[type + '_seller_id'] = event.id
    },
    deleteSpecialSeller(type, event) {
      if (confirm(this.$gettext('Are your sure you want to DELETE the chosen seller for this Company?\n\n***WARNING***\nThis action CANNOT BE UNDONE!'))) {
        this.company[type + '_seller_id'] = null
        this.updateCompany()
          .then(response => {
            this.getCompany()
          })
      }
    },
    async getCompanyStores() {
      let params = {
        includes: ['place'],
        filter_groups: [
          {
            filters: [
              ['company_id', 'eq', this.$route.params.id]
            ]
          }
        ]
      }
      return this.$http.get(this.$appConfig.api.baseUrl + 'companies-stores', {
        params: params
      })
        .then(response => {
          this.company.stores = response.body.data
          return this.company.stores
        })
    },
    fileUploaded(obj) {
      this.companyDocument.full_path = obj.full_path
      this.companyDocument.type = 'sla'
    },
    createCompanyDocument() {
      this.$http.post(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/documents', this.companyDocument)
        .then(response => {
          this.$refs.modalCompanyDocumentUpload.hide()
          this.getCompany()
        })
    },
    deleteCompanyDocument(item) {
      if (confirm(this.$gettext('Are your sure you want to DELETE the chosen document for this Company?\n\n***WARNING***\nThis action CANNOT BE UNDONE!'))) {
        this.$http.delete(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/documents/' + item.id)
          .then(response => {
            this.getCompany()
          })
      }
    },
    updateCutTimeWeek() {
      if (this.company.cut_time_week && this.company.cut_time_week.split(':').length < 3) {
        this.company.cut_time_week += ':00'
      }

      this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id, {
        cut_time_week: this.company.cut_time_week
      })
        .then(response => {
          this.$refs.modalUploadImage.hide()
          this.showAlert()
          this.getCompany()
        }, error => {
          let message = ''
          if (error.status === 422) {
            for (let i = 0; i < error.body.errors.length; i++) {
              message += '- ' + error.body.errors[i].detail + '<br>'
            }
          } else {
            message = error.body.message
          }
          let obj = {
            title: this.$gettext('ERROR'),
            message: message,
            type: 'error'
          }
          this.$parent.$refs.simplert.openSimplert(obj)
          this.getCompany()
        })
    },
    updateCutTimeSaturday() {
      if (this.company.cut_time_saturday && this.company.cut_time_saturday.split(':').length < 3) {
        this.company.cut_time_saturday += ':00'
      }

      this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id, {
        cut_time_saturday: this.company.cut_time_saturday
      })
        .then(response => {
          this.$refs.modalUploadImage.hide()
          this.showAlert()
          this.getCompany()
        }, error => {
          let message = ''
          if (error.status === 422) {
            for (let i = 0; i < error.body.errors.length; i++) {
              message += '- ' + error.body.errors[i].detail + '<br>'
            }
          } else {
            message = error.body.message
          }
          let obj = {
            title: this.$gettext('ERROR'),
            message: message,
            type: 'error'
          }
          this.$parent.$refs.simplert.openSimplert(obj)
          this.getCompany()
        })
    },
    updateCutTimeSunday() {
      if (this.company.cut_time_sunday && this.company.cut_time_sunday.split(':').length < 3) {
        this.company.cut_time_sunday += ':00'
      }

      this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id, {
        cut_time_sunday: this.company.cut_time_sunday
      })
        .then(response => {
          this.$refs.modalUploadImage.hide()
          this.showAlert()
          this.getCompany()
        }, error => {
          let message = ''
          if (error.status === 422) {
            for (let i = 0; i < error.body.errors.length; i++) {
              message += '- ' + error.body.errors[i].detail + '<br>'
            }
          } else {
            message = error.body.message
          }
          let obj = {
            title: this.$gettext('ERROR'),
            message: message,
            type: 'error'
          }
          this.$parent.$refs.simplert.openSimplert(obj)
          this.getCompany()
        })
    },
    async updateCompany() {
      this.doUpdateCompany()
        .then(() => {
          return true
        })
    },
    async doUpdateCompany() {
      if (!this.isSuperAdmin && !this.isAccountant) {
        delete this.company.tin
      }
      if (this.isSelfService) {
        delete this.company.billing_details
      }
      delete this.company.country_iso_code
      delete this.company.cut_time_week
      delete this.company.cut_time_saturday
      delete this.company.cut_time_sunday
      delete this.company.fixed_amounts
      delete this.company.operators
      delete this.company.sellers
      delete this.company.stores
      delete this.company.quotes
      delete this.company.users
      delete this.company.vehicles

      return await this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id, this.company).then(response => {
        this.$refs.modalUploadImage.hide()
        this.showAlert()
        this.prevSpecialSellers = {
          hunter: {},
          regional: {}
        }
        if (JSON.stringify(this.marketingSettings.sort()) !== JSON.stringify(this.company.marketing_settings.map(x => {
          return x.setting.id
        }).sort())) {
          this.updateMarketingSettings()
            .then(() => {
              this.getCompany()
            })
        } else {
          this.getCompany()
        }
      }, error => {
        let message = ''
        if (error.status === 422) {
          for (let i = 0; i < error.body.errors.length; i++) {
            message += '- ' + error.body.errors[i].detail + '<br>'
          }
        } else {
          message = error.body.message
        }
        let obj = {
          title: this.$gettext('ERROR'),
          message: message,
          type: 'error'
        }
        this.$parent.$refs.simplert.openSimplert(obj)
      })
    },
    updateCompanyImage(url) {
      this.company.logo = url
      this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id, {
        logo: url
      }).then(response => {
        this.$refs.modalUploadImage.hide()
        this.showAlert()
      }, error => {
        let obj = {
          title: this.$gettext('ERROR'),
          message: error.body.message,
          type: 'error'
        }
        this.$parent.$refs.simplert.openSimplert(obj)
      })
    },
    async updateMarketingSettings() {
      if (this.company.marketing_settings.length > 0) {
        this.company.marketing_settings.forEach((item, i) => {
          this.$http.delete(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/marketing-settings/' + item.id)
            .then(() => {
              if (this.company.marketing_settings.length === i + 1) {
                let payload
                this.marketingSettings.forEach((item, i) => {
                  payload = {
                    company_id: this.company.id,
                    marketing_catalog_id: item
                  }
                  this.$http.post(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/marketing-settings', payload)
                    .then(response => {
                      if (this.marketingSettings.length === i + 1) {
                        return true
                      }
                    })
                })
              }
            })
        })
      } else {
        let payload
        this.marketingSettings.forEach((item, i) => {
          payload = {
            company_id: this.company.id,
            marketing_catalog_id: item
          }
          this.$http.post(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/marketing-settings', payload)
            .then(response => {
              if (this.marketingSettings.length === i + 1) {
                return true
              }
            })
        })
      }
    },
    updateAccount() {
      let payload = {
        credit_limit: this.company.account.credit_limit,
        credit_limit_notification: this.company.account.credit_limit_notification
      }
      this.$http.patch(this.$appConfig.api.baseUrl + 'accounts/' + this.company.account.id, payload)
        .then(response => {
          this.$refs.modalUploadImage.hide()
          this.showAlert()
          this.getCompany()
        }, error => {
          let message = ''
          if (error.status === 422) {
            for (let i = 0; i < error.body.errors.length; i++) {
              message += '- ' + error.body.errors[i].detail + '<br>'
            }
          } else {
            message = error.body.message
          }
          let obj = {
            title: this.$gettext('ERROR'),
            message: message,
            type: 'error'
          }
          this.$parent.$refs.simplert.openSimplert(obj)
        })
    },
    updateBalance() {
      if (confirm(this.$gettext('Are your sure you want to SET A NEW BALANCE AMOUNT for this Company?\n\n***WARNING***\nThis action CANNOT BE UNDONE!'))) {
        this.$http.patch(this.$appConfig.api.baseUrl + 'accounts/' + this.company.account.id + '/balance', {
          balance: this.balance
        })
          .then(response => {
            this.balance = 0
            this.editingBalance = false
            this.getCompany()
          })
      }
    },
    fetchAccountingStuff() {
      let params = {
        sort: [{
          key: 'name',
          direction: 'asc'
        }],
        filter_groups: [
          {
            filters: [
              ['country_iso_code', 'eq', this.company.country_iso_code],
              ['catalog', 'in', ['customer_types', 'document_types', 'states', 'tax_conditions', 'payment_periods', 'invoicing_programs']]
            ]
          }
        ]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'accounting-catalogs', { params: params })
        .then(response => {
          this.accountingCatalogs = response.body.data
        })
    },
    updateCompanySetting(setting) {
      // Cleanup empty IP ranges
      if (Array.isArray(setting.companySetting.value)) {
        setting.companySetting.value = setting.companySetting.value.map(x => {
          if (x && x !== '...') {
            return x
          }
        }).filter(Boolean)
      }
      if (!setting.companySetting.id) {
        this.$http.post(this.$appConfig.api.baseUrl + 'company-settings', setting.companySetting)
          .then(response => {
            this.getCompany()
          })
      } else {
        this.$http.patch(this.$appConfig.api.baseUrl + 'company-settings/' + setting.companySetting.id, setting.companySetting)
          .then(response => {
            this.getCompany()
          })
      }
    },
    deleteCompanySetting(setting) {
      if (confirm(this.$gettext('Are your sure you want to DELETE this Company Setting?'))) {
        this.$http.delete(this.$appConfig.api.baseUrl + 'company-settings/' + setting.companySetting.id)
          .then(response => {
            this.getCompany()
          })
      }
    },
    changeNotification(notification, media, $e) {
      const notificationConfigId = this.company.notification_configs.findIndex(x => x.notification_type_id === notification && x.media === media) !== -1 ? this.company.notification_configs.find(x => x.notification_type_id === notification && x.media === media).id : null

      this.$nextTick(() => {
        let payload = {
          company_id: this.company.id,
          notification_type_id: notification,
          media: media,
          enabled: $e
        }
        if (!notificationConfigId) {
          this.$http.post(this.$appConfig.api.baseUrl + 'company-notification-configs', payload)
            .then(() => {
              this.showAlert()
              this.getCompany()
            })
        } else {
          this.$http.patch(this.$appConfig.api.baseUrl + 'company-notification-configs/' + notificationConfigId, payload)
            .then(() => {
              this.showAlert()
              this.getCompany()
            })
        }
      })
    },
    deleteVehicle(vehicle) {
      if (
        confirm(this.$gettextInterpolate(this.$gettext('Are you sure you want to DELETE the vehicle with Reference:\n%{reference}\nWARNING: THIS ACTION CANNOT BE UNDONE!'), {reference: vehicle.reference}))
      ) {
        this.$http.delete(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/vehicles' + '/' + vehicle.id)
          .then(response => {
            this.getCompany()
          })
      }
    },
    quoteCreated() {
      this.$refs.modalAddQuote.hide()
      this.getCompanyQuotes()
    },
    quoteUpdated() {
      this.$refs.modalEditCompanyQuote.hide()
      this.getCompanyQuotes()
    },
    quoteServicesCreated() {
      this.$refs.modalAddQuoteServices.hide()
      this.getCompanyQuoteServices()
    },
    quoteServicesUpdated() {
      this.$refs.modalEditCompanyQuoteServices.hide()
      this.getCompanyQuoteServices()
    },
    fixedPriceSuccess() {
      this.getCompany()
      this.$refs.modalFixedPrice.hide()
    },
    fixedPriceCancel() {
      this.$refs.modalFixedPrice.hide()
    },
    fetchCompanyLog(page = 1) {
      let params = {
        limit: 25,
        page: page
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + this.$route.params.id + '/audit', { params })
        .then(response => {
          response.body.data = response.body.data.map(x => {
            const targetKeys = Object.keys(x).filter((x) => /target_/.test(x))
            let targets = []
            for (const key in x) {
              if (Object.hasOwnProperty.call(x, key) && targetKeys.indexOf(key) !== -1 && key.indexOf('_accountId') === -1 && key.indexOf('_appliedBy') === -1 && key.indexOf('_priorData') === -1) {
                targets.push(this.isJson({ [key]: x[key] }))
              }
            }
            x = {
              change: ('<strong>' + this.camelCaseToTitleCase(x.event) + '</strong>' + (targets.length > 0 ? targets : '')).replace(/<\/ul>,<ul>/g, '').replace(/target_/g, ''),
              applied_by: {
                name: x.src_user_name
              },
              created_at: x.created_at
            }
            return x
          })
          this.companyLog = response.body
        })
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
    }
  }
}
</script>
